import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import { messageFromError } from "../../../shared/api/drfErrors";

import { fetchDeals, isRequestAbortError } from "../api/deals";
import { getDealStatusLabel } from "../lib/dealStatusLabels";
import {
  DEAL_COMPENSATION_CURRENCY_FILTERS,
  DEAL_COMPENSATION_FILTERS,
  DEAL_ORDERING_OPTIONS,
  DEAL_ROLE_FILTERS,
  DEAL_STATUS_FILTERS,
  buildDealListFetchParams,
  buildDealListSearchParams,
  dealListHasActiveFilters,
  dealListSearchNeedsCanonicalization,
  readDealListQueryParams,
} from "../lib/dealListQuery";
import {
  DEAL_LIST_PAGE_SIZE,
  totalDealListPages,
} from "../lib/dealListPagination";
import {
  getViewerRoleLabel,
} from "../lib/dealLifecycleActions";
import {
  formatCounterpartSummary,
  getCounterpartSidePresentation,
} from "../lib/dealSidePresentation";
import {
  dealTypeLabel,
  formatCreatedDateUk,
  formatDealCompensation,
  formatMoney,
  propertyTitleFromDeal,
} from "../lib/dealFormatting";

function DealPropertySummary({ deal }) {
  const title = propertyTitleFromDeal(deal, null);
  const dealType = deal.property_deal_type_snapshot
    ? dealTypeLabel(deal.property_deal_type_snapshot)
    : null;
  return (
    <div className="feature-deals-list__property-fallback">
      <span className="feature-deals-list__property-fallback-title">{title}</span>
      {dealType ? (
        <span className="feature-deals-list__property-subline">{dealType}</span>
      ) : null}
      {!deal.property ? (
        <span className="feature-deals-list__property-fallback-note">
          Знімок об&apos;єкта (живий об&apos;єкт недоступний)
        </span>
      ) : null}
    </div>
  );
}

function DealListCard({ deal }) {
  const counterpart = getCounterpartSidePresentation(deal);
  const counterpartLabel = formatCounterpartSummary(counterpart);
  const isCompanyCounterpart = counterpart && counterpart.kind === "company";
  const clientName =
    deal.client_summary && deal.client_summary.name
      ? deal.client_summary.name
      : null;

  return (
    <article className="feature-deals-list__card" data-testid="deal-list-card">
      <div className="feature-deals-list__card-top">
        <DealPropertySummary deal={deal} />
        <span
          className={[
            "feature-deals-list__status-badge",
            `feature-deals-list__status-badge--${deal.status}`,
          ].join(" ")}
        >
          {getDealStatusLabel(deal.status)}
        </span>
      </div>
      <dl className="feature-deals-list__card-facts">
        <div>
          <dt>Моя роль</dt>
          <dd>{getViewerRoleLabel(deal.viewer_role)}</dd>
        </div>
        <div>
          <dt>Контрагент</dt>
          <dd
            className={
              isCompanyCounterpart
                ? "feature-deals-list__counterpart--company"
                : undefined
            }
            data-counterpart-kind={
              counterpart ? counterpart.kind : "unknown"
            }
          >
            {isCompanyCounterpart ? (
              <>
                <span className="feature-deals-list__counterpart-kind">
                  Компанія
                </span>
                <span className="feature-deals-list__counterpart-name">
                  {counterpartLabel}
                </span>
              </>
            ) : (
              counterpartLabel
            )}
          </dd>
        </div>
        {clientName ? (
          <div>
            <dt>Клієнт</dt>
            <dd>{clientName}</dd>
          </div>
        ) : null}
        <div>
          <dt>Ціна</dt>
          <dd className="feature-deals-list__cell-numeric">
            {formatMoney(deal.price, deal.currency)}
          </dd>
        </div>
        <div>
          <dt>Винагорода партнеру</dt>
          <dd className="feature-deals-list__cell-numeric">
            {formatDealCompensation(deal)}
          </dd>
        </div>
        <div>
          <dt>Створено</dt>
          <dd className="feature-deals-list__cell-numeric">
            {formatCreatedDateUk(deal.created_at)}
          </dd>
        </div>
        {deal.updated_at ? (
          <div>
            <dt>Оновлено</dt>
            <dd className="feature-deals-list__cell-numeric">
              {formatCreatedDateUk(deal.updated_at)}
            </dd>
          </div>
        ) : null}
      </dl>
      <Link to={`/deals/${deal.id}`} className="action-btn">
        Відкрити
      </Link>
    </article>
  );
}

function NewDealGuideModal({ open, onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    const previous = document.activeElement;
    // Mobile layout scrolls #Csection (overflow-y: scroll), not only body.
    // Lock both without rewriting Navbar/MainLayout; restore exact prior inline styles.
    const csection =
      (dialogRef.current && dialogRef.current.closest("#Csection"))
      || document.getElementById("Csection");
    const surfaces = [document.body];
    if (csection) surfaces.push(csection);

    const previousStyles = surfaces.map((element) => ({
      element,
      overflow: element.style.overflow,
      overflowX: element.style.overflowX,
      overflowY: element.style.overflowY,
    }));

    surfaces.forEach((element) => {
      element.style.overflow = "hidden";
      element.style.overflowX = "hidden";
      element.style.overflowY = "hidden";
    });

    closeRef.current?.focus();

    const getFocusable = () => {
      const root = dialogRef.current;
      if (!root) return [];
      return Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => {
        if (element.hasAttribute("disabled")) return false;
        if (element.getAttribute("aria-hidden") === "true") return false;
        return true;
      });
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const root = dialogRef.current;
      const index = focusable.indexOf(active);
      const outside = !root || !root.contains(active) || index < 0;

      event.preventDefault();
      if (event.shiftKey) {
        if (outside || active === first) {
          last.focus();
        } else {
          focusable[index - 1].focus();
        }
      } else if (outside || active === last) {
        first.focus();
      } else {
        focusable[index + 1].focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previousStyles.forEach(({ element, overflow, overflowX, overflowY }) => {
        element.style.overflow = overflow;
        element.style.overflowX = overflowX;
        element.style.overflowY = overflowY;
      });
      if (previous && typeof previous.focus === "function") {
        previous.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="feature-deals-list__guide"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deal-new-guide-title"
      data-testid="deal-new-guide-modal"
      data-modal-layer="application"
    >
      <div
        className="feature-deals-list__guide-backdrop"
        aria-hidden="true"
        onClick={() => onCloseRef.current()}
      />
      <div className="feature-deals-list__guide-dialog" ref={dialogRef}>
        <header className="feature-deals-list__guide-head">
          <h2 id="deal-new-guide-title">Нова угода</h2>
          <button
            ref={closeRef}
            type="button"
            className="feature-deals-list__guide-close"
            onClick={() => onCloseRef.current()}
            aria-label="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-deals-list__guide-body">
          <p>
            Угода створюється з об&apos;єкта нерухомості — окремого майстра
            створення немає.
          </p>
          <p>
            Зовнішня пропозиція потребує чужого об&apos;єкта зі статусом
            «Активне» з увімкненою «Винагорода партнеру». Внутрішня угода
            створюється з власного об&apos;єкта зі статусом «Активне» або
            «Зарезервовано»; особистий дохід ведеться окремо в «Мої фінанси».
          </p>
          <div className="feature-deals-list__guide-actions">
            <Link
              to="/search"
              className="action-btn"
              onClick={() => onCloseRef.current()}
              data-testid="deal-new-guide-foreign"
            >
              Об&apos;єкт іншого рієлтора
            </Link>
            <Link
              to="/profile"
              className="action-btn"
              onClick={() => onCloseRef.current()}
              data-testid="deal-new-guide-own"
            >
              Власний об&apos;єкт
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function DealsListScreenContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = readDealListQueryParams(searchParams);
  const hasFilters = dealListHasActiveFilters(query);

  const [queryDraft, setQueryDraft] = useState(query.q);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [deals, setDeals] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });
  const [retryToken, setRetryToken] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const fetchInFlightRef = useRef(false);
  const appliedQRef = useRef(query.q);

  // Local UI only — viewport width for mobile placeholder / panel chrome.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const media = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsNarrowViewport(Boolean(media.matches));
    sync();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  // Keep local search draft aligned when URL applied search changes (Back/Forward/reset).
  useEffect(() => {
    if (appliedQRef.current !== query.q) {
      appliedQRef.current = query.q;
      setQueryDraft(query.q);
    }
  }, [query.q]);

  // Rewrite dirty /deals query via replace — no push, no fetch loop.
  useEffect(() => {
    if (!dealListSearchNeedsCanonicalization(searchParams)) {
      return;
    }
    setSearchParams(
      buildDealListSearchParams(readDealListQueryParams(searchParams)),
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const controller = new AbortController();
    fetchInFlightRef.current = true;
    let recoveryStarted = false;

    async function loadPage() {
      setLoadState((prev) => (prev === "loaded" && refreshing ? prev : "loading"));
      setLoadError(null);

      try {
        const payload = await fetchDeals(
          buildDealListFetchParams(query),
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;

        const pageDeals = Array.isArray(payload.results) ? payload.results : [];
        setDeals(pageDeals);
        setPagination({
          count: payload.count || 0,
          next: payload.next || null,
          previous: payload.previous || null,
        });
        setLoadState("loaded");
      } catch (error) {
        if (controller.signal.aborted || isRequestAbortError(error)) return;

        const status = error && error.response && error.response.status;
        // DRF PageNumberPagination returns HTTP 404 for an invalid page.
        // Recover once via a page-1 probe; never loop on non-404 failures.
        if (status === 404 && query.page > 1 && !recoveryStarted) {
          recoveryStarted = true;
          try {
            const probe = await fetchDeals(
              buildDealListFetchParams({ ...query, page: 1 }),
              { signal: controller.signal },
            );
            if (controller.signal.aborted) return;

            const count = probe.count || 0;
            if (count <= 0) {
              setSearchParams(
                buildDealListSearchParams({ ...query, page: 1 }),
                { replace: true },
              );
              return;
            }

            const lastPage = totalDealListPages(count, DEAL_LIST_PAGE_SIZE);
            setSearchParams(
              buildDealListSearchParams({ ...query, page: lastPage }),
              { replace: true },
            );
            return;
          } catch (probeError) {
            if (controller.signal.aborted || isRequestAbortError(probeError)) {
              return;
            }
            setLoadError(messageFromError(probeError));
            setLoadState("error");
            return;
          }
        }

        setLoadError(messageFromError(error));
        setLoadState("error");
      } finally {
        if (!controller.signal.aborted) {
          fetchInFlightRef.current = false;
          setRefreshing(false);
        }
      }
    }

    loadPage();
    return () => {
      controller.abort();
      fetchInFlightRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- canonical query fields + retry
  }, [
    query.page,
    query.q,
    query.status,
    query.role,
    query.compensation,
    query.compensation_currency,
    query.ordering,
    retryToken,
  ]);

  const replaceQuery = (patch) => {
    const next = buildDealListSearchParams({
      ...query,
      ...patch,
    });
    setSearchParams(next, { replace: true });
  };

  const goToPage = (page) => {
    replaceQuery({ page });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    replaceQuery({ q: queryDraft, page: 1 });
  };

  const handleReset = () => {
    setQueryDraft("");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const handleRefresh = () => {
    if (fetchInFlightRef.current || refreshing) return;
    setRefreshing(true);
    setRetryToken((value) => value + 1);
  };

  const totalPages = totalDealListPages(pagination.count, DEAL_LIST_PAGE_SIZE);
  const showPagination = loadState === "loaded" && deals.length > 0 && totalPages > 1;
  const activeChipFilterCount = [
    query.status,
    query.role,
    query.compensation,
    query.compensation_currency,
  ].filter(Boolean).length;
  const searchPlaceholder = isNarrowViewport
    ? "Ім’я або @handle"
    : "Пошук за імʼям або @handle партнера…";
  const currencyFilterDisabled = query.compensation === "none";

  const emptyDescription = hasFilters
    ? "Змініть пошук, статус, роль, винагороду партнеру або валюту, щоб побачити інші угоди."
    : "Тут зʼявлятимуться угоди, де ви є учасником.";

  return (
    <div className="feature-deals-list" data-feature="deals-list">
      <section className="clients-overview" aria-label="Огляд угод">
        <header className="clients-header">
          <div className="clients-header__text">
            <h1 className="clients-header__title">Угоди</h1>
            <p className="clients-header__sub">
              Угоди, де ви є учасником — пошук, фільтри та відкриття деталей.
            </p>
          </div>
          <div className="clients-header__actions">
            <button
              type="button"
              className="btn-more"
              onClick={() => setGuideOpen(true)}
              data-testid="deal-new-guide-open"
            >
              <i className="fa-solid fa-handshake" aria-hidden="true" />
              Нова угода
            </button>
          </div>
        </header>
      </section>

      <div className="clients-workspace__filters feature-deals-list__filters-shell">
        <form
          className="clients-toolbar feature-deals-list__toolbar"
          onSubmit={handleSearchSubmit}
          role="search"
          aria-label="Фільтри угод"
        >
          <div className="clients-toolbar__row feature-deals-list__toolbar-utility">
            <label className="clients-search feature-deals-list__search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <input
                type="search"
                name="q"
                value={queryDraft}
                onChange={(event) => setQueryDraft(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label="Пошук за імʼям або @handle партнера"
                data-testid="deal-list-search-input"
              />
            </label>

            <label className="feature-deals-list__ordering clients-toolbar__sort">
              <span className="clients-filter-group__title">Сортування</span>
              <div className="feature-deals-list__select-wrap">
                <select
                  className="feature-deals-list__select"
                  value={query.ordering || "-created_at"}
                  onChange={(event) =>
                    replaceQuery({ ordering: event.target.value, page: 1 })
                  }
                  aria-label="Сортування угод"
                >
                  {DEAL_ORDERING_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </label>

            <div className="feature-deals-list__toolbar-actions">
              <button type="submit" className="action-btn">
                Знайти
              </button>
              <button
                type="button"
                className="action-btn"
                onClick={handleRefresh}
                disabled={refreshing}
                aria-label="Оновити"
                title="Оновити"
              >
                {refreshing ? (
                  <Spinner size="sm" label="Оновлення" />
                ) : (
                  <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
                )}
              </button>
              {hasFilters || query.ordering ? (
                <button
                  type="button"
                  className="action-btn"
                  onClick={handleReset}
                >
                  Скинути
                </button>
              ) : null}
            </div>
          </div>

          <div className="feature-deals-list__filters-mobile-bar">
            <button
              type="button"
              className="action-btn feature-deals-list__filters-toggle"
              aria-expanded={filtersPanelOpen}
              aria-controls="deal-list-filters-panel"
              data-testid="deal-list-filters-toggle"
              onClick={() => setFiltersPanelOpen((open) => !open)}
            >
              Фільтри
              {activeChipFilterCount > 0 ? (
                <span
                  className="feature-deals-list__filters-count"
                  data-testid="deal-list-filters-count"
                >
                  {activeChipFilterCount}
                </span>
              ) : null}
            </button>
          </div>

          <div
            id="deal-list-filters-panel"
            className="clients-toolbar__row clients-toolbar__row--sub feature-deals-list__toolbar-filters feature-deals-list__filters-panel"
            data-open={filtersPanelOpen ? "" : undefined}
            data-testid="deal-list-filters-panel"
          >
            <div
              className="clients-filter-group"
              role="radiogroup"
              aria-label="Статус угоди"
            >
              <span className="clients-filter-group__title">Статус</span>
              <div className="clients-toolbar__pills">
                {DEAL_STATUS_FILTERS.map((chip) => {
                  const on = query.status === chip.value;
                  return (
                    <label
                      key={chip.value || "all-status"}
                      className="filter-chip"
                      data-on={on ? "" : undefined}
                    >
                      <input
                        type="radio"
                        name="deal-status"
                        checked={on}
                        onChange={() =>
                          replaceQuery({ status: chip.value, page: 1 })
                        }
                      />
                      <span>{chip.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div
              className="clients-filter-group"
              role="radiogroup"
              aria-label="Моя роль в угоді"
            >
              <span className="clients-filter-group__title">Моя роль</span>
              <div className="clients-toolbar__pills">
                {DEAL_ROLE_FILTERS.map((chip) => {
                  const on = query.role === chip.value;
                  return (
                    <label
                      key={chip.value || "all-roles"}
                      className="filter-chip"
                      data-on={on ? "" : undefined}
                    >
                      <input
                        type="radio"
                        name="deal-role"
                        checked={on}
                        onChange={() =>
                          replaceQuery({ role: chip.value, page: 1 })
                        }
                      />
                      <span>{chip.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div
              className="clients-filter-group"
              role="radiogroup"
              aria-label="Винагорода партнеру"
            >
              <span className="clients-filter-group__title">
                Винагорода партнеру
              </span>
              <div className="clients-toolbar__pills">
                {DEAL_COMPENSATION_FILTERS.map((chip) => {
                  const on = query.compensation === chip.value;
                  return (
                    <label
                      key={chip.value || "all-compensation"}
                      className="filter-chip"
                      data-on={on ? "" : undefined}
                    >
                      <input
                        type="radio"
                        name="deal-compensation"
                        checked={on}
                        onChange={() => {
                          const next = {
                            compensation: chip.value,
                            page: 1,
                          };
                          // «Немає» clears currency in the same URL update.
                          if (chip.value === "none") {
                            next.compensation_currency = "";
                          }
                          replaceQuery(next);
                        }}
                      />
                      <span>{chip.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {!currencyFilterDisabled ? (
              <label
                className="clients-filter-group feature-deals-list__compensation-currency"
              >
                <span className="clients-filter-group__title">Валюта винагороди</span>
                <div className="feature-deals-list__select-wrap">
                  <select
                    className="feature-deals-list__select"
                    value={query.compensation_currency || ""}
                    onChange={(event) =>
                      replaceQuery({
                        compensation_currency: event.target.value,
                        page: 1,
                      })
                    }
                    aria-label="Валюта винагороди партнеру"
                    data-testid="deal-list-compensation-currency"
                  >
                    {DEAL_COMPENSATION_CURRENCY_FILTERS.map((item) => (
                      <option key={item.value || "all-currency"} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            ) : null}
          </div>
        </form>
      </div>

      {loadState === "loading" ? (
        <div className="feature-deals-list__state">
          <Spinner size="lg" label="Завантаження угод" />
        </div>
      ) : null}

      {loadState === "error" ? (
        <div className="feature-deals-list__state">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити угоди"
            description={loadError || "Спробуйте оновити сторінку."}
            size="md"
            primaryAction={
              <button
                type="button"
                className="action-btn"
                onClick={() => setRetryToken((value) => value + 1)}
              >
                Спробувати знову
              </button>
            }
          />
        </div>
      ) : null}

      {loadState === "loaded" && deals.length === 0 ? (
        <div className="feature-deals-list__state">
          <EmptyState
            icon="fa-solid fa-handshake"
            title={hasFilters ? "За цим фільтром угод немає" : "Угод поки немає"}
            description={emptyDescription}
            size="md"
          />
        </div>
      ) : null}

      {loadState === "loaded" && deals.length > 0 ? (
        <>
          <p className="feature-deals-list__count" aria-live="polite">
            Всього угод: {pagination.count}
          </p>

          <div className="clients-table-wrap feature-deals-list__desktop">
            <table className="clients-table feature-deals-list__table">
              <caption className="visually-hidden">Список угод</caption>
              <thead>
                <tr>
                  <th scope="col">Об&apos;єкт</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Моя роль</th>
                  <th scope="col">Контрагент</th>
                  <th scope="col">Клієнт</th>
                  <th scope="col">Ціна</th>
                  <th scope="col">Винагорода партнеру</th>
                  <th scope="col">Створено</th>
                  <th scope="col">Дії</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((deal) => {
                  const counterpart = getCounterpartSidePresentation(deal);
                  const counterpartLabel = formatCounterpartSummary(counterpart);
                  const isCompanyCounterpart =
                    counterpart && counterpart.kind === "company";
                  const clientName =
                    deal.client_summary && deal.client_summary.name
                      ? deal.client_summary.name
                      : "—";
                  return (
                    <tr key={deal.id} className="feature-deals-list__row">
                      <td>
                        <DealPropertySummary deal={deal} />
                      </td>
                      <td>
                        <span
                          className={[
                            "feature-deals-list__status-badge",
                            `feature-deals-list__status-badge--${deal.status}`,
                          ].join(" ")}
                        >
                          {getDealStatusLabel(deal.status)}
                        </span>
                      </td>
                      <td>{getViewerRoleLabel(deal.viewer_role)}</td>
                      <td>
                        <span
                          className={[
                            "feature-deals-list__cell-text",
                            isCompanyCounterpart
                              ? "feature-deals-list__counterpart--company"
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          data-counterpart-kind={
                            counterpart ? counterpart.kind : "unknown"
                          }
                        >
                          {isCompanyCounterpart ? (
                            <>
                              <span className="feature-deals-list__counterpart-kind">
                                Компанія
                              </span>
                              {" "}
                              {counterpartLabel}
                            </>
                          ) : (
                            counterpartLabel
                          )}
                        </span>
                      </td>
                      <td>
                        <span className="feature-deals-list__cell-text">
                          {clientName}
                        </span>
                      </td>
                      <td>
                        <span className="feature-deals-list__cell-numeric">
                          {formatMoney(deal.price, deal.currency)}
                        </span>
                      </td>
                      <td>
                        <span className="feature-deals-list__cell-numeric">
                          {formatDealCompensation(deal)}
                        </span>
                      </td>
                      <td>
                        <span className="feature-deals-list__cell-numeric">
                          {formatCreatedDateUk(deal.created_at)}
                        </span>
                      </td>
                      <td>
                        <Link to={`/deals/${deal.id}`} className="action-btn">
                          Відкрити
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="feature-deals-list__mobile" data-testid="deal-list-mobile">
            {deals.map((deal) => (
              <DealListCard key={deal.id} deal={deal} />
            ))}
          </div>

          {showPagination ? (
            <NumberedPagination
              currentPage={query.page}
              totalPages={totalPages}
              hasPrevious={Boolean(pagination.previous)}
              hasNext={Boolean(pagination.next)}
              onPageChange={goToPage}
              ariaLabel="Пагінація списку угод"
              disabled={refreshing}
            />
          ) : null}
        </>
      ) : null}

      <NewDealGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}

export default DealsListScreenContent;
